import React, { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Backdrop,
} from '@mui/material'
import Timeline from '@mui/lab/Timeline'
import TimelineItem from '@mui/lab/TimelineItem'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import TimelineConnector from '@mui/lab/TimelineConnector'
import TimelineContent from '@mui/lab/TimelineContent'
import TimelineDot from '@mui/lab/TimelineDot'
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RefreshIcon from '@mui/icons-material/Refresh'
import PersonIcon from '@mui/icons-material/Person'
import FlagIcon from '@mui/icons-material/Flag'
import FolderIcon from '@mui/icons-material/Folder'
import TaskIcon from '@mui/icons-material/CheckCircle'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PublicIcon from '@mui/icons-material/Public'
import StarIcon from '@mui/icons-material/Star'
import CallSplitIcon from '@mui/icons-material/CallSplit'
import LinearProgress from '@mui/material/LinearProgress'
import { useForm } from 'react-hook-form'
import FragmentIdeaDialog from './workflows/FragmentIdeaDialog'
import { toast } from 'react-toastify'
import moment from 'moment'
import { roadmapAPI } from './api/roadmapAPI'
import LinkItemDialog from './workflows/LinkItemDialog'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

const DOMAIN_COLORS = {
  PROFESSIONAL: '#1976d2',
  FINANCE: '#2e7d32',
  HEALTH: '#d32f2f',
  PERSONAL: '#ed6c02',
  SOCIAL: '#9c27b0',
  HOME: '#0288d1',
  ADMIN: '#616161',
  TIME: '#7b1fa2',
  SPIRITUAL: '#c2185b',
  SPORTS: '#f57c00',
  LEISURE: '#00acc1',
  FOOTPRINT: '#388e3c',
  OTHER: '#757575',
}

// LAP-733 (GE022): labelKey/labelKeys arrays resolved via t() at render
const DOMAINS = [
  { value: 'PROFESSIONAL', labelKey: 'catProfessional' },
  { value: 'FINANCE',      labelKey: 'catFinance' },
  { value: 'HEALTH',       labelKey: 'catHealth' },
  { value: 'PERSONAL',     labelKey: 'catPersonalAlt' },
  { value: 'SOCIAL',       labelKey: 'catSocial' },
  { value: 'HOME',         labelKey: 'catHomeEnv' },
  { value: 'ADMIN',        labelKey: 'catAdmin' },
  { value: 'TIME',         labelKey: 'catTime' },
  { value: 'SPIRITUAL',    labelKey: 'catSpiritual' },
  { value: 'SPORTS',       labelKey: 'catSports' },
  { value: 'LEISURE',      labelKey: 'catLeisure' },
  { value: 'FOOTPRINT',    labelKey: 'catFootprint' },
  { value: 'OTHER',        labelKey: 'catOther' },
]

const IDEA_TYPES = [
  { value: 'ACCOMPLISH',  labelKey: 'ideaTypeAccomplish' },
  { value: 'BECOME',      labelKey: 'ideaTypeBecome' },
  { value: 'PROGRESS',    labelKey: 'ideaTypeProgress' },
  { value: 'EXPERIENCE',  labelKey: 'ideaTypeExperience' },
]

const HORIZONS = [
  { value: 'DREAMS',        labelKey: 'horizonDreams' },
  { value: 'YEAR_CURRENT',  labelKey: 'horizonYearCurrent' },
  { value: 'YEAR_NEXT',     labelKey: 'horizonYearNext' },
  { value: 'YEAR_5',        labelKey: 'horizonYear5' },
  { value: 'YEAR_10',       labelKey: 'horizonYear10' },
  { value: 'COMPLETED',     labelKey: 'horizonCompleted' },
  { value: 'ABANDONED',     labelKey: 'horizonAbandoned' },
]

const STATUS_KEYS = {
  IDEA: 'statusIdea',
  PLANNED: 'statusPlanned',
  ENGAGED: 'statusEngaged',
  COMPLETED: 'statusCompleted',
  ABANDONED: 'statusAbandoned',
}

const STATUS_COLORS = {
  IDEA: 'rgba(255, 255, 255, 0.3)',
  PLANNED: '#1565c0',
  ENGAGED: '#4caf50',
  COMPLETED: '#9c27b0',
  ABANDONED: '#f44336',
}

const EVENT_TYPE_KEYS = {
  CREATED: 'eventCreated',
  UPDATED: 'eventUpdated',
  MOVED_HORIZON: 'eventMovedHorizon',
  ROLLOVER_PROPOSED: 'eventRolloverProposed',
  ROLLOVER_APPLIED: 'eventRolloverApplied',
  ROLLOVER_ITEM_REVIEWED: 'eventRolloverItemReviewed',
  FRAGMENTED: 'eventFragmented',
  COMPLETED: 'eventCompleted',
  ABANDONED: 'eventAbandoned',
}

const HORIZON_LABEL_KEYS = {
  DREAMS: 'horizonDreams',
  YEAR_CURRENT: 'horizonYearCurrent',
  YEAR_NEXT: 'horizonYearNext',
  YEAR_5: 'horizonYear5',
  YEAR_10: 'horizonYear10',
  COMPLETED: 'horizonCompleted',
  ABANDONED: 'horizonAbandoned',
}

const IdeaDetailDrawer = ({ open, onClose, idea, onUpdate, onOpenIdea }) => {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const { register, handleSubmit, setValue, watch, reset } = useForm()
  const [loading, setLoading] = useState(false)
  const [links, setLinks] = useState([])
  const [showLinkDialog, setShowLinkDialog] = useState(false)
  const [events, setEvents] = useState([])
  const [loadingEvents, setLoadingEvents] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showFragmentDialog, setShowFragmentDialog] = useState(false)
  const [fragments, setFragments] = useState([])
  const [parentIdea, setParentIdea] = useState(null)
  const [loadingFragments, setLoadingFragments] = useState(false)
  const currentYear = new Date().getFullYear()
  
  const getHorizonLabel = (horizon) => {
    const key = HORIZON_LABEL_KEYS[horizon]
    const baseLabel = key ? t('roadmap.' + key) : horizon
    if (horizon === 'YEAR_CURRENT') {
      return `${baseLabel} (${currentYear})`
    } else if (horizon === 'YEAR_NEXT') {
      return `${baseLabel} (${currentYear + 1})`
    } else if (horizon === 'YEAR_5') {
      return `${baseLabel} (${currentYear + 5})`
    } else if (horizon === 'YEAR_10') {
      return `${baseLabel} (${currentYear + 10})`
    }
    return baseLabel
  }

  const fetchLinks = useCallback(async () => {
    if (!idea?.id) {
      setLinks([])
      return
    }
    try {
      const ideaLinks = await roadmapAPI.getIdeaLinks(idea.id)
      setLinks(ideaLinks)
    } catch (error) {
      console.error('Error fetching links:', error)
      setLinks([])
    }
  }, [idea?.id])

  const handleDeleteLink = async (linkId) => {
    if (!linkId) return
    
    try {
      setLoading(true)
      await roadmapAPI.deleteLink(linkId)
      toast.success(t('roadmap.toastLinkDeleted'))
      // Refresh links
      await fetchLinks()
      // Refresh idea to get updated status
      if (idea?.id) {
        try {
          const updatedIdea = await roadmapAPI.getIdea(idea.id)
          if (onUpdate) {
            onUpdate()
          }
        } catch (error) {
          console.error('Error refreshing idea:', error)
        }
      }
    } catch (error) {
      console.error('Error deleting link:', error)
      toast.error(t('roadmap.toastErrLinkDelete'))
    } finally {
      setLoading(false)
    }
  }

  const fetchEvents = useCallback(async () => {
    if (!idea?.id) {
      setEvents([])
      return
    }
    try {
      setLoadingEvents(true)
      const ideaEvents = await roadmapAPI.getIdeaEvents(idea.id)
      // Sort by date, newest first
      const sortedEvents = ideaEvents.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      )
      setEvents(sortedEvents)
    } catch (error) {
      console.error('Error fetching events:', error)
      setEvents([])
    } finally {
      setLoadingEvents(false)
    }
  }, [idea?.id])

  const fetchFragments = useCallback(async () => {
    if (!idea?.id) {
      setFragments([])
      setParentIdea(null)
      return
    }
    try {
      setLoadingFragments(true)
      // Fetch child ideas (fragments)
      const childIdeas = await roadmapAPI.getIdeaFragments(idea.id)
      setFragments(childIdeas)
      
      // Fetch parent idea if this idea has a parent
      // Handle both snake_case and camelCase field names
      const parentId = idea.parent_idea || idea.parentIdea
      if (parentId) {
        try {
          const parent = await roadmapAPI.getIdea(parentId)
          setParentIdea(parent)
        } catch (error) {
          console.error('Error fetching parent idea:', error)
          setParentIdea(null)
        }
      } else {
        setParentIdea(null)
      }
    } catch (error) {
      console.error('Error fetching fragments:', error)
      setFragments([])
      setParentIdea(null)
    } finally {
      setLoadingFragments(false)
    }
  }, [idea?.id, idea?.parent_idea])

  const formatEventDescription = (event) => {
    const { event_type, payload } = event
    const resolveHorizon = (h) => {
      const k = HORIZON_LABEL_KEYS[h]
      return k ? t('roadmap.' + k) : (h || t('roadmap.horizonDreams'))
    }

    switch (event_type) {
      case 'CREATED':
        return t('roadmap.eventDescCreated', { horizon: resolveHorizon(payload.horizon) })

      case 'MOVED_HORIZON': {
        const oldHorizon = resolveHorizon(payload.old_horizon)
        const newHorizon = resolveHorizon(payload.new_horizon)
        return t('roadmap.eventDescMovedHorizon', { from: oldHorizon, to: newHorizon })
      }

      case 'UPDATED':
        return t('roadmap.eventDescUpdated')

      case 'FRAGMENTED':
        return t('roadmap.eventDescFragmented', { count: payload.fragments_count || 0 })

      case 'COMPLETED':
        return t('roadmap.eventDescCompleted')

      case 'ABANDONED':
        return t('roadmap.eventDescAbandoned')

      default: {
        const k = EVENT_TYPE_KEYS[event_type]
        return k ? t('roadmap.' + k) : event_type
      }
    }
  }

  useEffect(() => {
    if (idea && open) {
      reset({
        title: idea.title || '',
        description: idea.description || '',
        domain: idea.domain || '',
        idea_type: idea.idea_type || '',
        horizon: idea.horizon || 'DREAMS',
      })
      // Fetch links, events, and fragments for this idea
      fetchLinks()
      fetchEvents()
      fetchFragments()
    } else {
      setLinks([])
      setEvents([])
      setFragments([])
      setParentIdea(null)
    }
  }, [idea, open, reset, fetchLinks, fetchEvents, fetchFragments])

  const onSubmit = async (data) => {
    if (!idea) return

    try {
      setLoading(true)
      
      // Check if anything actually changed
      const horizonChanged = data.horizon && data.horizon !== idea.horizon
      const titleChanged = data.title !== idea.title
      const descriptionChanged = data.description !== idea.description
      const domainChanged = data.domain !== idea.domain
      const ideaTypeChanged = data.idea_type !== idea.idea_type
      
      const hasChanges = horizonChanged || titleChanged || descriptionChanged || domainChanged || ideaTypeChanged
      
      if (!hasChanges) {
        toast.info(t('roadmap.toastNoChangesDetected'))
        onClose()
        return
      }
      
      // Update idea data
      await roadmapAPI.updateIdea(idea.id, data)

      // Only move horizon if it actually changed
      if (horizonChanged) {
        await roadmapAPI.moveHorizon(idea.id, data.horizon)
      }

      // Refresh idea data to get updated status
      try {
        const updatedIdea = await roadmapAPI.getIdea(idea.id)
        // Refresh events to show the update
        await fetchEvents()
      } catch (error) {
        console.error('Error refreshing idea:', error)
      }

      toast.success(t('roadmap.toastIdeaUpdated'))
      
      // Close drawer first to avoid DOM conflicts with drag-and-drop
      onClose()
      
      // Update parent component after drawer is fully closed
      // This prevents react-beautiful-dnd from trying to manage nodes being removed
      setTimeout(() => {
        if (onUpdate) {
          onUpdate()
        }
      }, 400)
    } catch (error) {
      toast.error(t('roadmap.toastErrUpdate'))
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!idea) return
    setShowDeleteConfirm(true)
  }

  const confirmDelete = async () => {
    if (!idea) return

    try {
      await roadmapAPI.deleteIdea(idea.id)
      toast.success(t('roadmap.toastIdeaDeleted'))
      setShowDeleteConfirm(false)
      onUpdate()
      onClose()
    } catch (error) {
      toast.error(t('roadmap.toastErrDelete'))
      console.error(error)
    }
  }

  if (!idea) return null

  return (
    <>
      <Backdrop
        open={open}
        sx={{
          zIndex: (theme) => theme.zIndex.modal - 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backgroundImage: 'url(/small.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backdropFilter: 'blur(10px)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 29, 0.6)',
          },
        }}
      />
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '95%', sm: '600px' },
            maxWidth: '600px',
            backgroundColor: 'transparent',
            color: 'white',
            backgroundImage: 'linear-gradient(135deg, rgba(156, 39, 176, 0.7) 0%, rgba(255, 152, 0, 0.7) 100%), url(/small.png)',
            backgroundSize: 'cover, cover',
            backgroundPosition: 'center, center',
            backgroundBlendMode: 'overlay, normal',
            position: 'relative',
            borderRadius: '20px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            maxHeight: '95vh',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 29, 0.5)',
              zIndex: 0,
              borderRadius: '20px',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            pb: 2,
            position: 'relative',
            zIndex: 1,
            color: 'white',
            // Gradient from slight black to transparent (left to right)
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(to right, rgba(0, 0, 0, 0.3) 0%, transparent 100%)',
              pointerEvents: 'none',
              zIndex: 0,
            },
            '& > *': {
              position: 'relative',
              zIndex: 1,
            },
          }}
        >
          <Box sx={{ flex: 1 }}>
            {/* Title with icon */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              🏁
              <TextField
                {...register('title', { required: true })}
                value={watch('title') || ''}
                onChange={(e) => setValue('title', e.target.value)}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    color: 'white',
                  },
                }}
                sx={{
                  flex: 1,
                  '& .MuiInput-root': {
                    '&:before': { display: 'none' },
                    '&:after': { display: 'none' },
                    '&:hover:not(.Mui-disabled):before': { display: 'none' },
                  },
                }}
              />
              <IconButton 
                size="small"
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&:hover': { color: 'white' },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
            {/* Description */}
            <TextField
              {...register('description')}
              value={watch('description') || ''}
              onChange={(e) => setValue('description', e.target.value)}
              variant="standard"
              placeholder={t('roadmap.addDescriptionPlaceholder')}
              multiline
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontSize: '0.95rem',
                  color: 'rgba(255, 255, 255, 0.85)',
                  lineHeight: 1.6,
                },
              }}
              sx={{
                width: '100%',
                '& .MuiInput-root': {
                  '&:before': { display: 'none' },
                  '&:after': { display: 'none' },
                  '&:hover:not(.Mui-disabled):before': { display: 'none' },
                },
              }}
            />
          </Box>
          <IconButton 
            onClick={onClose} 
            sx={{ 
              color: 'white',
              ml: 2,
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent 
          sx={{ 
            p: 2, 
            position: 'relative', 
            zIndex: 1, 
            maxHeight: '80vh', 
            overflow: 'auto',
            // Gradient from slight black to transparent (left to right)
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(to right, rgba(0, 0, 0, 0.3) 0%, transparent 100%)',
              pointerEvents: 'none',
              zIndex: 0,
            },
            // Custom scrollbar styling (Word/Google Docs style)
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '4px',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.5)',
              },
            },
            // Firefox scrollbar
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.05)',
            // Remove all focus rings globally
            '& *:focus': {
              outline: 'none !important',
              boxShadow: 'none !important',
            },
            '& *:focus-visible': {
              outline: 'none !important',
              boxShadow: 'none !important',
            },
            '& input:focus': {
              outline: 'none !important',
              boxShadow: 'none !important',
            },
            '& input:focus-visible': {
              outline: 'none !important',
              boxShadow: 'none !important',
            },
            '& > *': {
              position: 'relative',
              zIndex: 1,
            },
          }}
        >

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

            {/* Attributes in two columns */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1, fontSize: '0.875rem', fontWeight: 500 }}>
                  {t('roadmap.fieldDomain')}
                </Typography>
                <FormControl fullWidth>
                  <Select
                    {...register('domain')}
                    value={watch('domain') || ''}
                    onChange={(e) => setValue('domain', e.target.value)}
                    renderValue={(selected) => {
                      const domain = DOMAINS.find(d => d.value === selected)
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PublicIcon sx={{ fontSize: '18px', color: 'rgba(255, 255, 255, 0.9)' }} />
                          <Typography sx={{ color: 'white' }}>
                            {domain?.labelKey ? t('roadmap.' + domain.labelKey) : selected}
                          </Typography>
                        </Box>
                      )
                    }}
                    sx={{
                      color: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      borderRadius: '8px',
                      position: 'relative',
                      outline: 'none !important',
                      boxShadow: 'none !important',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '8px',
                        zIndex: 0,
                      },
                      '& .MuiSelect-select': {
                        position: 'relative',
                        zIndex: 1,
                        outline: 'none !important',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 14px',
                      },
                      
                      '& .MuiSvgIcon-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        position: 'relative',
                        zIndex: 1,
                      },
                    }}
                  >
                    {DOMAINS.map((d) => (
                      <MenuItem key={d.value} value={d.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PublicIcon sx={{ fontSize: '18px' }} />
                          {t('roadmap.' + d.labelKey)}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1, fontSize: '0.875rem', fontWeight: 500 }}>
                  {t('roadmap.ideaType')}
                </Typography>
                <FormControl fullWidth>
                  <Select
                    {...register('idea_type')}
                    value={watch('idea_type') || ''}
                    onChange={(e) => setValue('idea_type', e.target.value)}
                    renderValue={(selected) => {
                      const ideaType = IDEA_TYPES.find(it => it.value === selected)
                      return (
                        <Typography sx={{ color: 'white' }}>
                          {ideaType?.labelKey ? t('roadmap.' + ideaType.labelKey) : selected}
                        </Typography>
                      )
                    }}
                    sx={{
                      color: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.15)',
                      borderRadius: '8px',
                      position: 'relative',
                      outline: 'none !important',
                      boxShadow: 'none !important',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '8px',
                        zIndex: 0,
                      },
                      '& .MuiSelect-select': {
                        position: 'relative',
                        zIndex: 1,
                        outline: 'none !important',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 14px',
                      },
                      
                      '& .MuiSvgIcon-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        position: 'relative',
                        zIndex: 1,
                      },
                    }}
                  >
                    {IDEA_TYPES.map((it) => (
                      <MenuItem key={it.value} value={it.value}>
                        {t('roadmap.' + it.labelKey)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5, fontSize: '0.875rem' }}>
                {t('roadmap.currentHorizon')}
              </Typography>
              <FormControl fullWidth>
                <Select
                  {...register('horizon')}
                  value={watch('horizon') || 'DREAMS'}
                  onChange={(e) => setValue('horizon', e.target.value)}
                  renderValue={(selected) => {
                    return (
                      <Typography sx={{ color: 'white' }}>
                        {getHorizonLabel(selected)}
                      </Typography>
                    )
                  }}
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    borderRadius: '8px',
                    position: 'relative',
                    outline: 'none !important',
                    boxShadow: 'none !important',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      borderRadius: '8px',
                      zIndex: 0,
                    },
                    '& .MuiSelect-select': {
                      position: 'relative',
                      zIndex: 1,
                      outline: 'none !important',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px 14px',
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      position: 'relative',
                      zIndex: 1,
                    },
                  }}
                >
                  {HORIZONS.map((h) => (
                    <MenuItem key={h.value} value={h.value}>
                      {getHorizonLabel(h.value)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Status Display */}
            {idea.status && (
              <Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1, fontSize: '0.875rem', fontWeight: 500 }}>
                  {t('roadmap.statusLabel')}
                </Typography>
                <Chip
                  label={STATUS_KEYS[idea.status] ? t('roadmap.' + STATUS_KEYS[idea.status]) : idea.status}
                  sx={{
                    backgroundColor: STATUS_COLORS[idea.status] || 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontWeight: 600,
                    py: 1,
                    px: 1.5,
                    fontSize: '0.875rem',
                    height: 'auto',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(to right, rgba(0, 0, 0, 0.3) 0%, transparent 100%)',
                      borderRadius: 'inherit',
                      pointerEvents: 'none',
                    },
                    '& .MuiChip-label': {
                      position: 'relative',
                      zIndex: 1,
                    },
                  }}
                />
              </Box>
            )}

            <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

            {/* Linked Elements - Direct sections (not accordion) */}
            {(() => {
              const goals = links.filter(l => l.link_type === 'GOAL' || l.content_object_info?.type === 'goal')
              const projects = links.filter(l => l.link_type === 'PROJECT' || l.content_object_info?.type === 'project')
              const tasks = links.filter(l => l.link_type === 'TASK' || l.content_object_info?.type === 'task')
              const habits = links.filter(l => l.link_type === 'HABIT' || l.content_object_info?.type === 'habit')

              const renderLinkSection = (title, icon, items, emptyText) => (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1, fontSize: '0.875rem', fontWeight: 500 }}>
                    {title}
                  </Typography>
                  {items.length === 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1.5,
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '8px',
                        border: '1px dashed rgba(255, 255, 255, 0.2)',
                        color: 'rgba(255, 255, 255, 0.5)',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.08)',
                        },
                      }}
                      onClick={() => setShowLinkDialog(true)}
                    >
                      {icon}
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {emptyText}
                      </Typography>
                      <ArrowForwardIcon sx={{ fontSize: '18px', color: 'rgba(255, 255, 255, 0.7)' }} />
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {items.map((link) => (
                        <Box
                          key={link.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            p: 1.5,
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '8px',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.08)',
                            },
                          }}
                        >
                          {icon}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ color: 'white', fontWeight: 500, mb: 0.5 }}>
                              {link.content_object_info?.name || link.link_type || t('roadmap.linkFallback')}
                            </Typography>
                            {link.content_object_info?.progress !== undefined && (
                              <Box sx={{ mt: 0.5 }}>
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mb: 0.5 }}>
                                  {link.content_object_info.progress_text || ''}
                                </Typography>
                                <LinearProgress
                                  variant="determinate"
                                  value={link.content_object_info.progress || 0}
                                  sx={{
                                    height: 4,
                                    borderRadius: 2,
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    '& .MuiLinearProgress-bar': {
                                      backgroundColor: '#4caf50',
                                    },
                                  }}
                                />
                              </Box>
                            )}
                            {tasks.length > 0 && link === tasks[0] && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mb: 0.5 }}>
                                  {t('roadmap.tasksCompletedRatio', { done: tasks.filter(tt => tt.content_object_info?.completed).length, total: tasks.length })}
                                </Typography>
                                {tasks.slice(0, 1).map((task) => (
                                  <Typography key={task.id} variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block' }}>
                                    {task.content_object_info?.name || t('roadmap.taskCapital')}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteLink(link.id)
                              }}
                              sx={{
                                color: 'rgba(255, 255, 255, 0.7)',
                                '&:hover': {
                                  color: '#f44336',
                                  backgroundColor: 'rgba(244, 67, 54, 0.1)',
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                            <ArrowForwardIcon sx={{ fontSize: '18px', color: 'rgba(255, 255, 255, 0.7)' }} />
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )

              return (
                <>
                  {renderLinkSection(t('roadmap.linkedGoal'), <FlagIcon sx={{ color: '#ff9800', fontSize: '20px' }} />, goals, t('roadmap.noGoalLinkedHint'))}
                  {renderLinkSection(t('roadmap.linkedProject'), <StarIcon sx={{ color: '#2196f3', fontSize: '20px' }} />, projects, t('roadmap.noProjectLinkedHint'))}
                  {renderLinkSection(t('roadmap.tabTasks'), <TaskIcon sx={{ color: '#4caf50', fontSize: '20px' }} />, tasks, t('roadmap.noTaskLinkedHint'))}
                  {renderLinkSection(t('roadmap.habitsTitle'), <RefreshIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '20px' }} />, habits, t('roadmap.noHabitLinkedHint'))}
                </>
              )
            })()}

            {/* Parent/Child Relationships */}
            {(parentIdea || fragments.length > 0) && (
              <>
                <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                
                {parentIdea && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1, fontSize: '0.875rem', fontWeight: 500 }}>
                      {t('roadmap.parentIdea')}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1.5,
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                      onClick={async () => {
                        if (onOpenIdea && parentIdea) {
                          // Fetch fresh parent idea data and open it
                          try {
                            const freshParent = await roadmapAPI.getIdea(parentIdea.id)
                            onOpenIdea(freshParent)
                          } catch (error) {
                            console.error('Error fetching parent idea:', error)
                            // Fallback to using cached parent idea
                            onOpenIdea(parentIdea)
                          }
                        }
                      }}
                    >
                      <CallSplitIcon sx={{ color: '#9c27b0', fontSize: '20px' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                          {parentIdea.title}
                        </Typography>
                        {parentIdea.domain && (
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mt: 0.25 }}>
                            {parentIdea.domain.replace('_', ' ')}
                          </Typography>
                        )}
                      </Box>
                      <ArrowForwardIcon sx={{ fontSize: '18px', color: 'rgba(255, 255, 255, 0.7)' }} />
                    </Box>
                  </Box>
                )}

                {fragments.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Accordion
                      defaultExpanded={false}
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        color: 'white',
                        '&:before': {
                          display: 'none',
                        },
                        boxShadow: 'none',
                        '& .MuiAccordionSummary-root': {
                          minHeight: 48,
                          '&.Mui-expanded': {
                            minHeight: 48,
                          },
                        },
                        '& .MuiAccordionSummary-content': {
                          margin: '12px 0',
                          '&.Mui-expanded': {
                            margin: '12px 0',
                          },
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                        sx={{
                          '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                            transform: 'rotate(180deg)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CallSplitIcon sx={{ color: '#9c27b0', fontSize: '20px' }} />
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'white' }}>
                              {t('roadmap.fragmentsCount', { count: fragments.length })}
                            </Typography>
                          </Box>
                          {/* Progress Bar */}
                          {(() => {
                            const completedCount = fragments.filter(f => f.status === 'COMPLETED').length
                            const progress = fragments.length > 0 ? (completedCount / fragments.length) * 100 : 0
                            return (
                              <Box sx={{ mt: 0.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.75rem' }}>
                                    {t('roadmap.fragmentsCompletedRatio', { done: completedCount, total: fragments.length })}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.75rem' }}>
                                    {Math.round(progress)}%
                                  </Typography>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={progress}
                                  sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    '& .MuiLinearProgress-bar': {
                                      backgroundColor: '#4caf50',
                                      borderRadius: 3,
                                    },
                                  }}
                                />
                              </Box>
                            )
                          })()}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {fragments.map((fragment) => (
                            <Box
                              key={fragment.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                p: 1.5,
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                borderRadius: '8px',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                },
                              }}
                              onClick={async () => {
                                if (onOpenIdea && fragment) {
                                  // Fetch fresh fragment idea data and open it
                                  try {
                                    const freshFragment = await roadmapAPI.getIdea(fragment.id)
                                    onOpenIdea(freshFragment)
                                  } catch (error) {
                                    console.error('Error fetching fragment idea:', error)
                                    // Fallback to using cached fragment idea
                                    onOpenIdea(fragment)
                                  }
                                }
                              }}
                            >
                              <CallSplitIcon sx={{ color: '#9c27b0', fontSize: '20px' }} />
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                                    {fragment.title}
                                  </Typography>
                                  {fragment.status && (
                                    <Chip
                                      label={STATUS_KEYS[fragment.status] ? t('roadmap.' + STATUS_KEYS[fragment.status]) : fragment.status}
                                      size="small"
                                      sx={{
                                        backgroundColor: STATUS_COLORS[fragment.status] || 'rgba(255, 255, 255, 0.2)',
                                        color: 'white',
                                        fontWeight: 500,
                                        height: '18px',
                                        fontSize: '0.65rem',
                                        '& .MuiChip-label': {
                                          px: 0.5,
                                        },
                                      }}
                                    />
                                  )}
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                  {idea && (
                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block', fontStyle: 'italic' }}>
                                      {t('roadmap.fragmentOf')}: {idea.title}
                                    </Typography>
                                  )}
                                  {fragment.domain && (
                                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block' }}>
                                      {fragment.domain.replace('_', ' ')} • {HORIZON_LABEL_KEYS[fragment.horizon] ? t('roadmap.' + HORIZON_LABEL_KEYS[fragment.horizon]) : fragment.horizon}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              <ArrowForwardIcon sx={{ fontSize: '18px', color: 'rgba(255, 255, 255, 0.7)' }} />
                            </Box>
                          ))}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                )}
              </>
            )}

            <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

            {/* History Section - Collapsible Accordion */}
            <Accordion
              defaultExpanded={false}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
                '&:before': {
                  display: 'none',
                },
                boxShadow: 'none',
                '& .MuiAccordionSummary-root': {
                  minHeight: 48,
                  '&.Mui-expanded': {
                    minHeight: 48,
                  },
                },
                '& .MuiAccordionSummary-content': {
                  margin: '12px 0',
                  '&.Mui-expanded': {
                    margin: '12px 0',
                  },
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                sx={{
                  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
                    transform: 'rotate(180deg)',
                  },
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 500, color: 'white' }}>
                  {t('roadmap.historyTitle')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {loadingEvents ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 1.5 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : events.length === 0 ? (
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                  >
                    {t('roadmap.noHistoryAvailable')}
                  </Typography>
                ) : (
                  <Timeline
                    sx={{
                      '& .MuiTimelineItem-root:before': {
                        display: 'none',
                      },
                      padding: 0,
                    }}
                  >
                    {events.map((event, index) => (
                      <TimelineItem key={event.id}>
                        <TimelineOppositeContent
                          sx={{
                            flex: 0.3,
                            color: 'rgba(255, 255, 255, 0.6)',
                            fontSize: '0.75rem',
                          }}
                        >
                          {moment(event.created_at).format('DD/MM/YYYY HH:mm')}
                        </TimelineOppositeContent>
                        <TimelineSeparator>
                          <TimelineDot
                            sx={{
                              backgroundColor:
                                event.event_type === 'CREATED'
                                  ? '#4caf50'
                                  : event.event_type === 'MOVED_HORIZON'
                                  ? '#2196f3'
                                  : event.event_type === 'COMPLETED'
                                  ? '#9c27b0'
                                  : 'rgba(255, 255, 255, 0.3)',
                            }}
                          />
                          {index < events.length - 1 && (
                            <TimelineConnector
                              sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
                            />
                          )}
                        </TimelineSeparator>
                        <TimelineContent sx={{ py: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'white',
                              fontWeight: 500,
                              mb: 0.5,
                            }}
                          >
                            {EVENT_TYPE_KEYS[event.event_type] ? t('roadmap.' + EVENT_TYPE_KEYS[event.event_type]) : event.event_type}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                          >
                            {formatEventDescription(event)}
                          </Typography>
                        </TimelineContent>
                      </TimelineItem>
                    ))}
                  </Timeline>
                )}
              </AccordionDetails>
            </Accordion>

            <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

            {/* Footer */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {t('roadmap.createdOn')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'white' }}>
                  {moment(idea.created_at).format('DD/MM/YYYY')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  variant="contained"
                  startIcon={<CallSplitIcon />}
                  onClick={() => setShowFragmentDialog(true)}
                  sx={{ 
                    borderColor: '#6a1b9a',
                    color: 'white',
                    flex: 1,
                    position: 'relative',
                    backgroundColor: '#6a1b9a',
                    padding: '10px 16px',
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(to top, rgba(0, 0, 0, 0.4) 0%, transparent 100%)',
                      borderRadius: '4px',
                      zIndex: 0,
                    },
                    '& .MuiButton-label': {
                      position: 'relative',
                      zIndex: 1,
                    },
                    '&:hover': {
                      backgroundColor: '#5a1a8a',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 8px rgba(106, 27, 154, 0.4)',
                    },
                  }}
                >
                  {t('roadmap.btnFragment')}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDelete}
                  sx={{ 
                    borderColor: '#c62828',
                    color: 'white',
                    flex: 1,
                    position: 'relative',
                    backgroundColor: '#c62828',
                    padding: '10px 16px',
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(to top, rgba(0, 0, 0, 0.4) 0%, transparent 100%)',
                      borderRadius: '4px',
                      zIndex: 0,
                    },
                    '& .MuiButton-label': {
                      position: 'relative',
                      zIndex: 1,
                    },
                    '&:hover': {
                      backgroundColor: '#b71c1c',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 8px rgba(198, 40, 40, 0.4)',
                    },
                  }}
                >
                  {t('roadmap.btnDelete')}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={async () => {
                    try {
                      await roadmapAPI.updateIdea(idea.id, { horizon: 'COMPLETED' })
                      toast.success(t('roadmap.toastIdeaMarkedDone'))
                      if (onUpdate) {
                        onUpdate()
                      }
                      onClose()
                    } catch (error) {
                      toast.error(t('roadmap.toastErrUpdate'))
                      console.error(error)
                    }
                  }}
                  sx={{ 
                    flex: 1,
                    backgroundColor: '#2e7d32',
                    position: 'relative',
                    padding: '10px 16px',
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(to top, rgba(0, 0, 0, 0.4) 0%, transparent 100%)',
                      borderRadius: '4px',
                      zIndex: 0,
                    },
                    '& .MuiButton-label': {
                      position: 'relative',
                      zIndex: 1,
                    },
                    '&:hover': {
                      backgroundColor: '#1b5e20',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 8px rgba(46, 125, 50, 0.4)',
                    },
                  }}
                >
                  {t('roadmap.btnDone')}
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
                <Button
                  variant="contained"
                  onClick={onClose}
                  sx={{ 
                    borderColor: 'rgba(33, 33, 33, 0.8)',
                    color: 'white',
                    flex: 1,
                    position: 'relative',
                    backgroundColor: '#212121',
                    padding: '10px 16px',
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(to top, rgba(0, 0, 0, 0.4) 0%, transparent 100%)',
                      borderRadius: '4px',
                      zIndex: 0,
                    },
                    '& .MuiButton-label': {
                      position: 'relative',
                      zIndex: 1,
                    },
                    '&:hover': {
                      backgroundColor: '#1a1a1a',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 8px rgba(33, 33, 33, 0.4)',
                    },
                  }}
                >
                  {t('roadmap.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{ 
                    flex: 1,
                    position: 'relative',
                    backgroundColor: '#0d47a1',
                    padding: '10px 16px',
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(to top, rgba(0, 0, 0, 0.4) 0%, transparent 100%)',
                      borderRadius: '4px',
                      zIndex: 0,
                    },
                    '& .MuiButton-label': {
                      position: 'relative',
                      zIndex: 1,
                    },
                    '&:hover': {
                      backgroundColor: '#01579b',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(13, 71, 161, 0.4)',
                    },
                    '&:disabled': {
                      opacity: 0.6,
                    },
                  }}
                >
                  {t('roadmap.save')}
                </Button>
              </Box>
            </Box>
          </Box>
        </form>
        </DialogContent>
      </Dialog>
      <LinkItemDialog
        open={showLinkDialog}
        onClose={() => setShowLinkDialog(false)}
        ideaId={idea?.id}
        onLinkCreated={() => {
          fetchLinks()
          if (onUpdate) {
            onUpdate()
          }
        }}
      />

      <FragmentIdeaDialog
        open={showFragmentDialog}
        onClose={() => setShowFragmentDialog(false)}
        idea={idea}
        onFragmented={() => {
          fetchEvents()
          fetchFragments()
          if (onUpdate) {
            onUpdate()
          }
        }}
      />

      {/* Delete Confirmation Dialog */}
      <>
        <Backdrop
          open={showDeleteConfirm}
          sx={{
            zIndex: (theme) => theme.zIndex.modal - 1,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backgroundImage: 'url(/small.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backdropFilter: 'blur(10px)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 29, 0.6)',
            },
          }}
        />
        <Dialog
          open={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: 'transparent',
              color: 'white',
              backgroundImage: 'linear-gradient(135deg, rgba(156, 39, 176, 0.7) 0%, rgba(255, 152, 0, 0.7) 100%), url(/small.png)',
              backgroundSize: 'cover, cover',
              backgroundPosition: 'center, center',
              backgroundBlendMode: 'overlay, normal',
              position: 'relative',
              borderRadius: '20px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 29, 0.5)',
                zIndex: 0,
                borderRadius: '20px',
              },
            },
          }}
        >
        <DialogTitle
          sx={{
            position: 'relative',
            zIndex: 1,
            color: 'white',
            fontWeight: 600,
            fontSize: '1.25rem',
          }}
        >
          {t('roadmap.confirmDeleteTitle')}
        </DialogTitle>
        <DialogContent
          sx={{
            position: 'relative',
            zIndex: 1,
            color: 'white',
            // Custom scrollbar styling
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '4px',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.5)',
              },
            },
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.05)',
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              lineHeight: 1.6,
            }}
          >
            {t('roadmap.confirmDeleteBody')}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            position: 'relative',
            zIndex: 1,
            p: 2,
            gap: 1.5,
          }}
        >
          <Button
            onClick={() => setShowDeleteConfirm(false)}
            variant="outlined"
            sx={{
              borderColor: 'rgba(255, 255, 255, 0.4)',
              color: 'white',
              position: 'relative',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              padding: '10px 16px',
              fontSize: '0.95rem',
              fontWeight: 600,
              textTransform: 'none',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '4px',
                zIndex: 0,
              },
              '& .MuiButton-label': {
                position: 'relative',
                zIndex: 1,
              },
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.6)',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 8px rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            {t('roadmap.cancel')}
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            sx={{
              backgroundColor: '#c62828',
              position: 'relative',
              padding: '10px 16px',
              fontSize: '0.95rem',
              fontWeight: 600,
              textTransform: 'none',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.15)',
                borderRadius: '4px',
                zIndex: 0,
              },
              '& .MuiButton-label': {
                position: 'relative',
                zIndex: 1,
              },
              '&:hover': {
                backgroundColor: '#b71c1c',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 8px rgba(198, 40, 40, 0.4)',
              },
            }}
          >
            {t('roadmap.btnDelete')}
          </Button>
        </DialogActions>
      </Dialog>
      </>
    </>
  )
}

export default IdeaDetailDrawer

